/**
 * WT1 - starting a walk-through, `/tours/new` (plan section E; spec section 5). NOT in the nav: it is reached from
 * the Walk-throughs list and from the property page's "Walk-through" button (`?property=<id>` skips the search).
 *
 * ⛔ STARTED WITH SIGNAL, DELIBERATELY (approach B). Offline you cannot check whether the building is already in
 *   the app, and this database has already needed a 154-group duplicate merge (PD2) - a fully offline start would
 *   manufacture duplicates in a parking lot. So the property is picked, or created after a DUPLICATE CHECK, here;
 *   the tour id is the SERVER's; and only then does the phone take over. Tilt-up concrete kills signal inside; the
 *   lot has it.
 * ⭐ Where the phone is: asked ONCE, here, and never blocking (it reports a disagreement; it never refuses).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import { api, NetworkError } from '../lib/api';
import { currentLocation } from '../lib/geo';
import { getTourQueue, requestPersistentStorage } from '../lib/tourQueue';
import type { CreateTourBody, PropertyDuplicateMatch } from '../types/tours';

interface Picked {
  id: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip?: string | null;
  property_type?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

const H1 = 'font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]';
const LINK = 'text-[12.5px] font-semibold text-primary-500 hover:text-primary-600 hover:underline underline-offset-[3px]';
const FIELD = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-[14px]';

const labelOf = (p: Picked): string => [p.address, p.city].filter(Boolean).join(', ') || 'This property';

/** A coordinate as a number, or null - a numeric column can arrive as a string. */
const coord = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export default function StartTour() {
  const [params] = useSearchParams();
  const fromPage = params.get('property');
  const navigate = useNavigate();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; // StrictMode runs this twice in dev: set it back on the second mount
    return () => {
      mounted.current = false;
    };
  }, []);

  const [picked, setPicked] = useState<Picked | null>(null);
  const [loadingPicked, setLoadingPicked] = useState(Boolean(fromPage));
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Picked[]>([]);

  const [adding, setAdding] = useState(false);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('CA');
  const [zip, setZip] = useState('');
  const [twins, setTwins] = useState<PropertyDuplicateMatch[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);

  // From the property page: that building, no search.
  useEffect(() => {
    if (!fromPage) return;
    let live = true;
    api
      .getMasterProperty(fromPage)
      .then((r: { property?: Picked }) => {
        if (live && r?.property) setPicked(r.property);
      })
      .catch((err: unknown) => {
        if (live) setError(messageOf(err, 'That property could not be opened.'));
      })
      .finally(() => {
        if (live) setLoadingPicked(false);
      });
    return () => {
      live = false;
    };
  }, [fromPage]);

  // The property database, three letters in, a quarter-second after he stops typing.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setHits([]);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      api
        .searchMasterProperties(q, 8)
        .then((r: { properties?: Picked[] }) => {
          if (live) setHits(r?.properties ?? []);
        })
        .catch((err: unknown) => {
          if (live) setError(messageOf(err, 'The property search did not answer.'));
        });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createMasterProperty({ address: address.trim(), city: city.trim(), state: state.trim(), zip: zip.trim() });
      if (!mounted.current) return;
      setTwins(null);
      setAdding(false);
      setPicked(r.property as Picked);
    } catch (err) {
      if (mounted.current) setError(messageOf(err, 'The property could not be added.'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /** ⛔ The duplicate check runs BEFORE any record is made - PD2 needed a 154-group merge. */
  const addIt = async () => {
    setBusy(true);
    setError(null);
    try {
      const { matches } = await api.checkPropertyDuplicates(address.trim());
      if (!mounted.current) return;
      if (matches.length) {
        setTwins(matches);
        setBusy(false);
        return;
      }
    } catch (err) {
      if (mounted.current) {
        setError(messageOf(err, 'The duplicate check did not answer, so nothing was added.'));
        setBusy(false);
      }
      return;
    }
    await create();
  };

  const start = async () => {
    if (!picked) return;
    setStarting(true);
    setError(null);
    try {
      const where = await currentLocation();
      void requestPersistentStorage();
      const body: CreateTourBody = {
        master_property_id: picked.id,
        ...(where ? { start_lat: where.lat, start_lng: where.lng } : {}),
      };
      const { tour } = await api.createTour(body);
      const queue = await getTourQueue();
      await queue.saveTour({
        tourId: tour.id,
        masterPropertyId: picked.id,
        propertyLabel: labelOf(picked),
        propertyType: picked.property_type ?? null,
        propertyLat: coord(picked.latitude),
        propertyLng: coord(picked.longitude),
        startLat: where?.lat ?? null,
        startLng: where?.lng ?? null,
        touredAt: tour.toured_at,
      });
      navigate(`/tour/${tour.id}`);
    } catch (err) {
      if (!mounted.current) return;
      setError(
        err instanceof NetworkError
          ? 'Starting a walk-through needs signal - step out to the lot, where there usually is some. Once it has started, the phone keeps every photo with none.'
          : messageOf(err, 'The walk-through could not be started.'),
      );
    } finally {
      if (mounted.current) setStarting(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-xl space-y-5">
        <div>
          <h1 className={H1}>Start a walk-through</h1>
          <p className="mt-1 text-[13px] text-gray-500">
            Pick the building with signal - the lot is fine. From then on the phone keeps every photo, signal or not.
          </p>
        </div>

        {error && (
          <p role="alert" className="text-[13px] font-semibold text-red-700">
            {error}
          </p>
        )}

        {picked ? (
          <section aria-label="The property" className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
            <div>
              <p className="text-[16px] font-semibold text-gray-900">{labelOf(picked)}</p>
              <p className="text-[12.5px] text-gray-600">
                {[picked.state, picked.zip].filter(Boolean).join(' ')}
                {picked.property_type ? ` · ${picked.property_type}` : ''}
              </p>
            </div>
            <Button size="lg" className="w-full" onClick={() => void start()} isLoading={starting}>
              Start the walk-through
            </Button>
            {!fromPage && (
              <button type="button" className={LINK} onClick={() => setPicked(null)}>
                Choose a different property
              </button>
            )}
          </section>
        ) : loadingPicked ? (
          <p className="text-[13px] text-gray-600">Opening the property</p>
        ) : (
          <div className="space-y-4">
            {!adding && (
              <div>
                <label htmlFor="tour-find" className="block text-[12px] font-semibold text-gray-700">
                  Find the property
                </label>
                <input
                  id="tour-find"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Address, name or city"
                  className={FIELD}
                />
                {hits.length > 0 && (
                  <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {hits.map((p) => (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => setPicked(p)}
                          className="block w-full px-3 py-2.5 text-left text-[13.5px] text-gray-900 hover:bg-gray-50"
                        >
                          {[p.address, p.city, p.state].filter(Boolean).join(', ')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {adding ? (
              <section aria-label="Add the property" className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-[13px] text-gray-600">
                  A new record in the property database. It is checked against the ones already there first.
                </p>
                {[
                  ['tour-new-address', 'Address', address, setAddress],
                  ['tour-new-city', 'City', city, setCity],
                  ['tour-new-state', 'State', state, setState],
                  ['tour-new-zip', 'ZIP', zip, setZip],
                ].map(([id, label, value, set]) => (
                  <div key={id as string}>
                    <label htmlFor={id as string} className="block text-[12px] font-semibold text-gray-700">
                      {label as string}
                    </label>
                    <input
                      id={id as string}
                      value={value as string}
                      onChange={(e) => (set as (v: string) => void)(e.target.value)}
                      className={FIELD}
                    />
                  </div>
                ))}

                {twins ? (
                  <div className="space-y-2">
                    <p className="text-[13px] font-semibold text-gray-900">Is it one of these?</p>
                    {twins.map((t) => (
                      <Button
                        key={t.id}
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => {
                          setTwins(null);
                          setAdding(false);
                          setPicked(t);
                        }}
                      >
                        {`Use ${[t.address, t.city].filter(Boolean).join(', ')}`}
                      </Button>
                    ))}
                    <Button variant="outline" className="w-full" onClick={() => void create()} isLoading={busy}>
                      None of these - add it
                    </Button>
                  </div>
                ) : (
                  <Button onClick={() => void addIt()} disabled={!address.trim() || !city.trim()} isLoading={busy}>
                    Add it
                  </Button>
                )}
                <button
                  type="button"
                  className={`${LINK} block`}
                  onClick={() => {
                    setAdding(false);
                    setTwins(null);
                  }}
                >
                  Back to the search
                </button>
              </section>
            ) : (
              <button type="button" className={LINK} onClick={() => setAdding(true)}>
                Not in the app?
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
